/**
 * Личные настройки пользователя: вид доски, свой порядок и видимость колонок, сортировка, уведомления и звук, меню.
 * Хранятся на сервере (с любого браузера — те же), поверх общих настроек. Копия в браузере — чтобы доска
 * сразу открывалась в своём виде, ещё до ответа сервера. Изменения уходят на сервер с небольшой задержкой.
 *
 * Первый вход после появления личных настроек: на сервере пусто — переносим то, что было настроено
 * в этом браузере раньше (старые ключи localStorage), и сохраняем.
 */
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { defaultUserPrefs, userPrefsSchema, type UserPrefs } from '@sync-tracker/shared';
import { api } from '@/api';
import { useAuth } from './auth';

const SAVE_DELAY_MS = 500;

function readJson(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw == null ? undefined : JSON.parse(raw);
  } catch {
    return undefined;
  }
}
function readRaw(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Настройки, которые до этого хранились только в браузере, — в формате личных настроек. */
function legacyPrefs(userId: number | undefined): UserPrefs {
  const layout = readRaw('st-board-layout');
  const closed = readRaw('st-board-closed');
  return userPrefsSchema.parse({
    board: {
      layout: layout ?? undefined,
      hidden: readJson('st-board-hidden'),
      sort: readJson('st-board-sort'),
      showClosed: closed == null ? undefined : closed !== '0',
    },
    notify: readJson(`st-notify:${userId ?? 'anon'}`),
    ui: { sidebarCollapsed: readRaw('st-sidebar') === '1' },
  });
}

export const usePrefs = defineStore('prefs', () => {
  const auth = useAuth();
  const cacheKey = () => `st-prefs:${auth.user?.id ?? 'anon'}`;

  // сразу — из копии в браузере (или старых ключей), чтобы не мигал вид по умолчанию
  const prefs = ref<UserPrefs>(initial());
  const loaded = ref(false);
  function initial(): UserPrefs {
    const cached = readJson(cacheKey());
    return cached ? userPrefsSchema.parse(cached) : legacyPrefs(auth.user?.id);
  }

  let timer: ReturnType<typeof setTimeout> | null = null;
  let applying = false;

  async function load() {
    try {
      const res = await api.prefs.get();
      applying = true;
      if (res.saved) prefs.value = res.prefs;
      else {
        // на сервере ещё ничего нет — переносим настройки этого браузера
        prefs.value = userPrefsSchema.parse({ ...defaultUserPrefs(), ...legacyPrefs(auth.user?.id) });
        await api.prefs.save(prefs.value);
      }
    } catch {
      /* сервер недоступен — работаем с копией в браузере */
    } finally {
      applying = false;
      loaded.value = true;
    }
  }

  async function flush() {
    if (timer) clearTimeout(timer);
    timer = null;
    try {
      await api.prefs.save(prefs.value);
    } catch {
      /* не сохранилось — останется в копии браузера и уйдёт со следующим изменением */
    }
  }

  watch(
    prefs,
    (p) => {
      try {
        localStorage.setItem(cacheKey(), JSON.stringify(p));
      } catch {
        /* приватный режим */
      }
      if (applying || !loaded.value || !auth.user) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void flush(), SAVE_DELAY_MS);
    },
    { deep: true },
  );

  // уходя со страницы — не теряем последнее изменение
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => {
      if (!timer) return;
      clearTimeout(timer);
      timer = null;
      try {
        navigator.sendBeacon?.('/api/me/prefs/beacon', new Blob([JSON.stringify(prefs.value)], { type: 'application/json' }));
      } catch {
        /* нет sendBeacon */
      }
    });
  }

  /** Другой пользователь вошёл в этом браузере — перечитать его настройки. */
  function reset() {
    applying = true;
    prefs.value = initial();
    applying = false;
    loaded.value = false;
  }

  return { prefs, loaded, load, flush, reset };
});
